import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getMessagesByConversationId, saveAdminReply, updateConversation } from '../../../utils/messanger.controller';
import styles from './AdminChat.module.css';
import supabase from '../../../config/supabaseClient';
import LoginButton from '../../Design/Button/Login/LoginButton';
import InvisibleButton from '../../Design/Button/Invisible/Invisible';

function AdminChat() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Define these functions BEFORE they're used in useEffect
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Reset error when starting a new fetch
      // In a real application, you might need a specific endpoint for admin to fetch all conversations
      const { data, error: supabaseError } = await supabase
        .from('conversations')
        .select('*, users:user_id(fname, lname)')
        .order('updated_at', { ascending: false });
  
      if (supabaseError) throw supabaseError;
      
      setConversations(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
      setLoading(false);
    }
  }, [setError]); // Added setError as dependency

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      setLoading(true);
      setError(null); // Reset error when starting a new fetch
      const messagesData = await getMessagesByConversationId(conversationId);
      setMessages(messagesData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
      setLoading(false);
    }
  }, [setError]); // Added setError as dependency

  // Now use the functions in useEffect
  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchConversations();
    }
  }, [user, fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim() || !selectedConversation) {
      return;
    }

    try {
      setLoading(true);
      setError(null); // Reset error state
      
      await saveAdminReply({
        conversation_id: selectedConversation.id,
        admin_id: user.user_id,
        user_id: selectedConversation.user_id,
        message: replyText
      });

      // Clear reply input
      setReplyText('');
      
      // Refresh messages
      await fetchMessages(selectedConversation.id);
      
      setLoading(false);
    } catch (err) {
      console.error('Error sending reply:', err);
      setError('Failed to send reply');
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      setError(null); // Reset error state
      
      await updateConversation(selectedConversation.id, {
        status: newStatus
      });
      
      // Update local state
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv.id === selectedConversation.id 
            ? {...conv, status: newStatus} 
            : conv
        )
      );
      
      setSelectedConversation({
        ...selectedConversation,
        status: newStatus
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating conversation status:', err);
      setError('Failed to update status');
      setLoading(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return <div className={styles.unauthorized}>You don't have permission to access this page</div>;
  }

  return (
    <div className={styles.chatContainer}>
      {/* Display error message if there is one */}
      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}
      <div className={styles.chatSidebar}>
        <h2>Conversations</h2>
        <div className={styles.conversationList}>
          {conversations.map(conv => (
            <div 
              key={conv.id} 
              className={`${styles.conversationItem} ${selectedConversation?.id === conv.id ? styles.active : ''}`}
              onClick={() => setSelectedConversation(conv)}
            >
              <div className={styles.conversationHeader}>
                <span className={styles.userName}>{conv.users.fname} {conv.users.lname}</span>
                <span className={`${styles.statusIndicator} ${styles[conv.status]}`}></span>
              </div>
              <div className={styles.conversationSubject}>{conv.subject}</div>
              <div className={styles.conversationDate}>
                {new Date(conv.updated_at).toLocaleDateString()}
              </div>
            </div>
          ))}
          {conversations.length === 0 && !loading && (
            <div className={styles.emptyState}>No conversations yet</div>
          )}
        </div>
      </div>
      
      <div className={styles.chatMain}>
        {selectedConversation ? (
          <>
            <div className={styles.chatHeader}>
              <InvisibleButton
                onClick={() => handleStatusChange(selectedConversation.status === 'open' ? 'closed' : 'open')}
                text={selectedConversation.status === 'open' ? 'Close Conversation' : 'Open Conversation'}
                type="submit"
              />
              <div className={styles.conversationItem}>
                <h3>{selectedConversation.subject}</h3>
                <p>With: {selectedConversation.users.fname} {selectedConversation.users.lname}</p>
              </div>
              <div className={styles.chatActions}>
                <div className={styles.conversationList}>
                  {messages.map(msg => (
                    <div 
                      key={msg.id} 
                      className={`${styles.messageItem} ${msg.sender_user_id === user.user_id ? styles.sent : styles.received}`}
                    >
                      <div className={styles.messageContent}>
                        {msg.message_body}
                      
                        <div className={styles.messageTime}>
                          {new Date(msg.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            </div>
            <div className={styles.messageForm}>
              {selectedConversation.status === 'open' ? (
                <form onSubmit={handleSendReply}>
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    disabled={loading}
                  />
                  <LoginButton 
                    type="submit" 
                    disabled={loading || !replyText.trim()}
                    text="Reply"
                  />
                </form>
              ) : (
                <div className={styles.closedMessage}>
                  This conversation is closed. Reopen it to continue the discussion.
                </div>
              )}
            </div>
          </>
        ) : (
          <div className={styles.noChatSelected}>
            Select a conversation to view messages
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminChat;